// src/contexts/RightsContext.jsx
import React, { useState, useEffect, useContext, createContext, useCallback, useRef } from 'react';
import api from '../services/api';
import { useLoader } from './LoaderContext';

const RightsContext = createContext(null);

// Variable globale anti-doublon
let rightsPromise = null;

export const useRights = () => {
  const context = useContext(RightsContext);
  if (!context) {
    throw new Error('useRights doit être utilisé dans un RightsProvider');
  }
  return context;
};

export const RightsProvider = ({ children }) => {
  const [rights, setRights] = useState(null);
  const [loading, setLoading] = useState(true);

  const { showLoader, hideLoader } = useLoader();

  useEffect(() => {
    if (loading) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [loading]);
  

  const CACHE_KEY = 'horapilot_rights';
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // ============================================================================
  // Récupérer le userId courant depuis le token JWT
  // ============================================================================
  const getCurrentUserId = useCallback(() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.userId || payload.id || null;
    } catch {
      return null;
    }
  }, []);

  // ============================================================================
  // Cache lié au userId — empêche l'héritage entre utilisateurs
  // ============================================================================
  const getCached = useCallback(() => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const { data, timestamp, userId } = JSON.parse(raw);

      // Vérifier que c'est le même utilisateur
      const currentUserId = getCurrentUserId();
      if (userId && currentUserId && userId !== currentUserId) {
        console.log('🔄 [Rights] Cache invalidé — utilisateur différent');
        localStorage.removeItem(CACHE_KEY);
        return null;
      }

      // Vérifier expiration
      if (Date.now() - timestamp > CACHE_DURATION) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }

      return data;
    } catch {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
  }, [getCurrentUserId]);

  const setCache = useCallback((data) => {
    try {
      const userId = getCurrentUserId();
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        data,
        timestamp: Date.now(),
        userId
      }));
    } catch {}
  }, [getCurrentUserId]);

  // ============================================================================
  // Charger les droits depuis l'API
  // ============================================================================
  const loadRights = useCallback(async (forceRefresh = false) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setRights(null);
        setLoading(false);
        rightsPromise = null;
        return;
      }

      // Anti-doublon : réutiliser la promesse en cours
      if (rightsPromise && !forceRefresh) {
        await rightsPromise;
        return;
      }

      // Vérifier le cache (seulement si pas de forceRefresh)
      if (!forceRefresh) {
        const cached = getCached();
        if (cached) {
          console.log('📦 [Rights] Depuis le cache');
          setRights(cached);
          setLoading(false);
          return;
        }
      }

      console.log('🌐 [Rights] Appel API...');
      setLoading(true);
      rightsPromise = api.moduleRights.getMyRights();
      const response = await rightsPromise;

      if (response.success) {
        setRights(response.rights);
        setCache(response.rights);
        console.log('✅ [Rights] Droits chargés:', response.rights);
      } else {
        console.error('❌ [Rights] API a retourné success=false');
        setRights(null);
      }
    } catch (error) {
      console.error('❌ [Rights] Erreur:', error);
      // Fallback cache en cas d'erreur réseau
      const cached = getCached();
      if (cached) {
        setRights(cached);
      } else {
        setRights(null);
      }
    } finally {
      setLoading(false);
      rightsPromise = null;
    }
  }, [getCached, setCache]);

  // ============================================================================
  // Charger au montage — toujours forcer un appel API frais
  // Le cache a été vidé dans Login.jsx avant la redirection
  // ============================================================================
  useEffect(() => {
    // Vérifier si le cache appartient au bon utilisateur
    const cached = getCached();
    if (cached) {
      // Cache valide et même utilisateur → utiliser le cache
      loadRights(false);
    } else {
      // Pas de cache ou utilisateur différent → appel API
      loadRights(true);
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================================
  // Écouter les changements de token (autres onglets)
  // ============================================================================
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'token') {
        if (!e.newValue) {
          // Déconnexion dans un autre onglet
          setRights(null);
          localStorage.removeItem(CACHE_KEY);
        } else {
          // Nouvelle connexion dans un autre onglet
          loadRights(true);
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [loadRights]);

  // ============================================================================
  // Helpers
  // ============================================================================

  const hasRight = useCallback((module) => {
    if (!rights) return false;
    return rights[module] === true;
  }, [rights]);

  const isKioskMode = useCallback(() => {
    if (!rights) return false;
    return (
      rights.attendance === true &&
      rights.dashboard !== true &&
      rights.employees !== true &&
      rights.payroll !== true &&
      rights.reports !== true &&
      rights.documents !== true &&
      rights.settings !== true
    );
  }, [rights]);

  const getFirstAllowedModule = useCallback(() => {
    if (!rights) return null;
    const modules = [
      { key: 'dashboard', path: '/dashboard' },
      { key: 'employees', path: '/employees' },
      { key: 'attendance', path: '/attendance' },
      { key: 'payroll', path: '/payroll' },
      { key: 'reports', path: '/reports' },
      { key: 'documents', path: '/documents' },
      { key: 'settings', path: '/settings' }
    ];
    for (const mod of modules) {
      if (rights[mod.key] === true) return mod.path;
    }
    return null;
  }, [rights]);

  const refreshRights = useCallback(async () => {
    setLoading(true);
    localStorage.removeItem(CACHE_KEY);
    await loadRights(true);
  }, [loadRights]);

  const clearRights = useCallback(() => {
    setRights(null);
    setLoading(true);
    localStorage.removeItem(CACHE_KEY);
    rightsPromise = null;
  }, []);

  return (
    <RightsContext.Provider value={{
      rights,
      loading,
      hasRight,
      isKioskMode,
      getFirstAllowedModule,
      refreshRights,
      clearRights
    }}>
      {children}
    </RightsContext.Provider>
  );
};